// Theme Studio — 本番PWA内のビルダー（② 移植）。Bearer 認証下で Worker のアプリを開き、
// テーマ（パレット/ステータス色/角丸/base）を編集 → 本番ランタイムと同じ ViewRenderer でプレビュー → 公開。
import { useEffect, useState } from "preact/hooks";
import {
  builderAppSig,
  builderAppsSig,
  previewModeSig,
  builderStatusSig,
  editViewIdSig,
  loadBuilderApps,
  openBuilderApp,
  editBuilderToken,
  applyThemeColor,
  setBuilderBase,
  togglePreviewMode,
  publishBuilderApp,
  setEditView,
  changeViewType,
  changeViewSort,
  changeViewGroup,
  addViewField,
  removeViewBlock,
  moveViewBlock,
} from "./builder-store";
import { ViewRenderer } from "../render/View";
import { resolveTokens, cssVars, label, DEMO_TOKENS } from "../render/model.mjs";
import { IconPalette, IconRefresh, IconMoon, IconSun, IconCloudUp, IconExternal, IconArrowLeft, IconX, IconLayout } from "../render/icons";
import { config } from "../config";
import type { AppDefinition, View as ViewDef, Row, Column } from "../types";

// 編集できる色トークン（reference ビルダーと同一・SSOT は brand.tokens 契約）
const PALETTE = [
  { key: "color.brand.primary", label: "主色" },
  { key: "color.bg.app", label: "背景" },
  { key: "color.bg.card", label: "カード面" },
  { key: "color.fg.default", label: "文字" },
  { key: "color.fg.muted", label: "補助" },
  { key: "color.brand.mint", label: "アクセント" },
];
const STATUS = [
  { key: "color.chip.success.bg", label: "成功" },
  { key: "color.chip.warning.bg", label: "注意" },
  { key: "color.chip.danger.bg", label: "危険" },
  { key: "color.chip.info.bg", label: "情報" },
];
const RADIUS = ["8px", "10px", "12px", "14px", "16px", "20px"];

/** プレビューの起点ビュー（ランタイムの home 解決と同じ規則）。 */
function homeView(app: AppDefinition): ViewDef | null {
  const id = app.settings?.homeViewId ?? app.navigation?.[0]?.viewId ?? app.views?.[0]?.id ?? null;
  return id ? app.views.find((v) => v.id === id) ?? null : null;
}

/** プレビュー用のダミー行（実データ無しでも見た目を確認）。enum は選択肢を巡回。 */
function dummyRows(app: AppDefinition, view: ViewDef): Row[] {
  const table = app.tables?.find((t) => t.id === view.tableId);
  if (!table) return [];
  const mk = (i: number): Row => {
    const row: Row = { __rowId: `preview-${i}` };
    for (const c of table.columns as Column[]) {
      const key = c.sourceRef ?? c.id;
      if (c.type === "enum" && c.options?.length) row[key] = c.options[i % c.options.length].value;
      else if (c.type === "number") row[key] = (i + 1) * 10;
      else if (c.type === "phone") row[key] = "0312345678";
      else if (c.type === "date" || c.type === "datetime") row[key] = new Date().toISOString().slice(0, 10);
      else if (c.type === "boolean") row[key] = i % 2 === 0;
      else row[key] = `${label(c.name, "ja") || c.id} ${i + 1}`;
    }
    return row;
  };
  return [mk(0), mk(1), mk(2)];
}

export function BuilderView() {
  const app = builderAppSig.value;
  const apps = builderAppsSig.value;
  const mode = previewModeSig.value;
  const status = builderStatusSig.value;
  const [tab, setTab] = useState<"theme" | "views">("theme");

  useEffect(() => { void loadBuilderApps(); }, []);

  const defaults = DEMO_TOKENS as Record<string, string>;
  const tokenVal = (key: string): string =>
    (app?.brand?.tokens?.[key] as string | undefined) ?? defaults[key] ?? "#000000";
  const runtimeUrl = app ? `${location.origin}/?app=${encodeURIComponent(app.id)}` : "#";

  const editViewId = editViewIdSig.value;
  const view = app ? (app.views.find((v) => v.id === editViewId) ?? homeView(app)) : null;
  const table = app && view ? app.tables?.find((t) => t.id === view.tableId) : null;
  const cols: Column[] = table?.columns ?? [];
  const rows = app && view ? dummyRows(app, view) : [];
  const tokens = app ? resolveTokens(app, mode) : DEMO_TOKENS;
  // ビュー内の field ブロック（並べ替え/削除の対象）
  const fieldBlocks = (view?.layout?.blocks ?? []).filter((b) => b.component === "field" && b.binding);
  const usedColIds = new Set(fieldBlocks.map((b) => b.binding!.columnId));
  const addableCols = cols.filter((c) => !usedColIds.has(c.id));
  const VIEW_TYPES: Array<{ t: ViewDef["type"]; label: string }> = [
    { t: "list", label: "リスト" }, { t: "table", label: "テーブル" }, { t: "detail", label: "詳細" },
    { t: "form", label: "フォーム" }, { t: "gallery", label: "ギャラリー" }, { t: "board", label: "カンバン" },
    { t: "calendar", label: "カレンダー" }, { t: "map", label: "マップ" }, { t: "dashboard", label: "ダッシュボード" },
  ];
  const sort0 = view?.sort?.[0];

  const env = config.apiBase.includes("staging") ? "staging" : config.apiBase.includes("localhost") ? "local" : "prod";

  return (
    <div class="tstudio">
      {/* 上部ツールバー: アプリ選択と公開を常時表示 */}
      <header class="ts-topbar">
        <a class="ts-icon-btn" href="/" title="ランタイムへ戻る" aria-label="戻る"><IconArrowLeft size={17} /></a>
        <span class="ts-brand"><IconPalette size={16} /> Theme Studio</span>
        <span class="ts-env">{env}</span>
        <select
          class="ts-appsel"
          value={app?.id ?? ""}
          onChange={(e) => { const id = (e.target as HTMLSelectElement).value; if (id) void openBuilderApp(id); }}
        >
          <option value="">アプリを選択…（{apps.length}）</option>
          {apps.map((a) => <option key={a.id} value={a.id}>{a.name ?? a.id}</option>)}
        </select>
        <button class="ts-icon-btn" onClick={() => void loadBuilderApps()} title="一覧を再取得"><IconRefresh size={15} /></button>
        <span class="ts-flex" />
        {app && (
          <>
            {status.kind === "ok" && (
              <a class="ts-icon-btn" href={runtimeUrl} target="_blank" rel="noopener" title="ランタイムで開く"><IconExternal size={16} /></a>
            )}
            <button class="ts-publish-sm" onClick={() => void publishBuilderApp()} disabled={status.kind === "loading"}>
              <IconCloudUp size={15} /> 公開
            </button>
          </>
        )}
      </header>

      {status.msg && <div class={"ts-statusbar ts-" + status.kind}>{status.msg}</div>}

      {!app ? (
        <div class="ts-welcome">
          <IconLayout size={40} />
          <p>上の「アプリを選択」からアプリを開くと、<br />左で設定・右でプレビューできます。</p>
        </div>
      ) : (
        <div class="ts-workspace">
          {/* 左レール: 設定セクションの切替 */}
          <nav class="ts-rail">
            <button class={"ts-railbtn" + (tab === "theme" ? " is-on" : "")} onClick={() => setTab("theme")}>
              <IconPalette size={20} /><span>テーマ</span>
            </button>
            <button class={"ts-railbtn" + (tab === "views" ? " is-on" : "")} onClick={() => setTab("views")}>
              <IconLayout size={20} /><span>ビュー</span>
            </button>
          </nav>

          {/* 中央: 選択セクションの設定 */}
          <div class="ts-config">
            {tab === "theme" && (
              <>
                <div class="ts-card">
                  <span class="ts-card-h">テーマカラー（主色）</span>
                  <label class="ts-maincolor">
                    <input type="color" value={tokenVal("color.brand.primary")} onInput={(e) => applyThemeColor((e.target as HTMLInputElement).value)} />
                    <span>この1色を変えると、アクセントも連動して配色が変わります</span>
                  </label>
                </div>
                <div class="ts-card">
                  <span class="ts-card-h">ライト / ダーク</span>
                  <div class="ts-row">
                    {(["light", "dark", "auto"] as const).map((b) => (
                      <button key={b} class={"ts-chip" + (app.brand?.base === b ? " is-on" : "")} onClick={() => setBuilderBase(b)}>
                        {b === "light" ? "ライト" : b === "dark" ? "ダーク" : "自動"}
                      </button>
                    ))}
                  </div>
                </div>
                <div class="ts-card">
                  <span class="ts-card-h">ステータス色</span>
                  <div class="ts-sw">
                    {STATUS.map((t) => (
                      <label key={t.key} class="ts-swatch">{t.label}
                        <input type="color" value={tokenVal(t.key)} onInput={(e) => editBuilderToken(t.key, (e.target as HTMLInputElement).value)} />
                      </label>
                    ))}
                  </div>
                </div>
                <div class="ts-card">
                  <span class="ts-card-h">角丸（カード）</span>
                  <select class="ts-select" value={tokenVal("radius.lg")} onChange={(e) => editBuilderToken("radius.lg", (e.target as HTMLSelectElement).value)}>
                    {RADIUS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <details class="ts-card">
                  <summary class="ts-card-h">配色を個別に微調整</summary>
                  <div class="ts-sw" style="margin-top:8px">
                    {PALETTE.map((t) => (
                      <label key={t.key} class="ts-swatch">{t.label}
                        <input type="color" value={tokenVal(t.key)} onInput={(e) => editBuilderToken(t.key, (e.target as HTMLInputElement).value)} />
                      </label>
                    ))}
                  </div>
                </details>
              </>
            )}

            {tab === "views" && view && (
              <>
                <div class="ts-card">
                  <span class="ts-card-h">編集するビュー</span>
                  <select class="ts-select" value={view.id} onChange={(e) => setEditView((e.target as HTMLSelectElement).value)}>
                    {app.views.map((v) => <option key={v.id} value={v.id}>{label(v.name, "ja") || v.id}</option>)}
                  </select>
                </div>
                <div class="ts-card">
                  <span class="ts-card-h">ビュータイプ</span>
                  <div class="ts-typegrid">
                    {VIEW_TYPES.map((vt) => (
                      <button key={vt.t} class={"ts-typebtn" + (view.type === vt.t ? " is-on" : "")} onClick={() => changeViewType(vt.t)}>{vt.label}</button>
                    ))}
                  </div>
                </div>
                <div class="ts-card">
                  <span class="ts-card-h">並び替え</span>
                  <div class="ts-row">
                    <select class="ts-select" value={sort0?.columnId ?? ""} onChange={(e) => changeViewSort((e.target as HTMLSelectElement).value, sort0?.dir ?? "asc")}>
                      <option value="">（なし）</option>
                      {cols.map((c) => <option key={c.id} value={c.id}>{label(c.name, "ja") || c.id}</option>)}
                    </select>
                    <select class="ts-select" style="flex:0 0 88px" value={sort0?.dir ?? "asc"} disabled={!sort0} onChange={(e) => sort0 && changeViewSort(sort0.columnId, (e.target as HTMLSelectElement).value as "asc" | "desc")}>
                      <option value="asc">昇順</option>
                      <option value="desc">降順</option>
                    </select>
                  </div>
                </div>
                <div class="ts-card">
                  <span class="ts-card-h">グループ化</span>
                  <select class="ts-select" value={view.groupByColumnId ?? ""} onChange={(e) => changeViewGroup((e.target as HTMLSelectElement).value, "count")}>
                    <option value="">（なし）</option>
                    {cols.map((c) => <option key={c.id} value={c.id}>{label(c.name, "ja") || c.id}</option>)}
                  </select>
                </div>
                <div class="ts-card">
                  <span class="ts-card-h">表示ブロック（{fieldBlocks.length}）</span>
                  <div class="ts-blocks">
                    {fieldBlocks.map((b, i) => (
                      <div class="ts-block" key={b.id}>
                        <span class="ts-block-t">{label(cols.find((c) => c.id === b.binding!.columnId)?.name, "ja") || b.binding!.columnId}</span>
                        <button class="ts-mini" disabled={i === 0} title="上へ" onClick={() => moveViewBlock(b.id, "up")}>↑</button>
                        <button class="ts-mini" disabled={i === fieldBlocks.length - 1} title="下へ" onClick={() => moveViewBlock(b.id, "down")}>↓</button>
                        <button class="ts-mini ts-mini-x" title="削除" onClick={() => removeViewBlock(b.id)}><IconX size={13} /></button>
                      </div>
                    ))}
                  </div>
                  {addableCols.length > 0 && (
                    <select class="ts-select" style="margin-top:6px" value="" onChange={(e) => { const v = (e.target as HTMLSelectElement).value; if (v) { addViewField(v); (e.target as HTMLSelectElement).value = ""; } }}>
                      <option value="">＋ 列を表示に追加…</option>
                      {addableCols.map((c) => <option key={c.id} value={c.id}>{label(c.name, "ja") || c.id}</option>)}
                    </select>
                  )}
                </div>
              </>
            )}
          </div>

          {/* 右: 端末フレームのライブプレビュー */}
          <div class="ts-stage">
            <div class="ts-stage-head">
              <span>{view ? label(view.name, "ja") : ""}</span>
              <button class="ts-toggle" onClick={() => togglePreviewMode()} title="ライト/ダーク">
                {mode === "dark" ? <IconSun size={15} /> : <IconMoon size={15} />}
              </button>
            </div>
            <div class="ts-phone">
              <div class="ts-phone-screen" style={cssVars(tokens)}>
                {view ? (
                  <ViewRenderer app={app} view={view} rows={rows} locale="ja" mode={mode} evalCtx={{ now: new Date() }} />
                ) : (
                  <div class="ts-empty">表示できるビューがありません</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
